import * as yup from "yup";

import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Grid,
  GridProps,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { AppForm, FormField, FormFieldProps } from "@picovico/ui";
import { catchErrorHandler } from "@picovico/utils";
import { FaWhatsapp } from "react-icons/fa";

import { useAppToast } from "@/hooks/useAppToast";
import { useSubmitContactUsMutation } from "@/store/services/contact";
import { useMemo } from "react";

export type ContactFormFieldConfig = FormFieldProps & {
  fieldContainerProps?: BoxProps;
};

export interface ContactFormRowConfig {
  fields: ContactFormFieldConfig[];
  rowGridProps?: GridProps;
}

export interface ContactComponent1Props {
  containerProps?: BoxProps;
  titleText?: string;
  titleProps?: TextProps;
  subtitleText?: string;
  subtitleProps?: TextProps;
  contentGridProps?: GridProps;
  // goes to AppForm's formProps (controls the form Box)
  formContainerProps?: BoxProps;
  // shared styling applied to every FormField; individual field configs override
  formFieldProps?: FormFieldProps;
  formRows?: ContactFormRowConfig[];
  submitButtonProps?: ButtonProps;
  submitButtonLabel?: string;
  mapContainerProps?: BoxProps;
  mapSrc?: string;
  mapTitle?: string;
  // Social media contact feature: a hardcoded WhatsApp CTA button, off by
  // default so existing tenants (e.g. CineRoyale) are unaffected; the
  // destination link is fully config-driven.
  showSocialButton?: boolean;
  socialButtonLink?: string;
  socialButtonLabel?: string;
  socialButtonProps?: ButtonProps;
}

function buildValidationSchema(
  fields: ContactFormFieldConfig[],
): yup.AnyObjectSchema {
  const shape: Record<string, yup.AnySchema> = {};
  fields.forEach(({ name, label, type, required }) => {
    let schema: yup.AnySchema = type === "number" ? yup.number() : yup.string();
    if (type === "email") {
      schema = (schema as yup.StringSchema).email();
    }
    if (required) schema = schema.required();
    if (label) schema = schema.label(label);
    shape[name] = schema;
  });
  return yup.object().shape(shape);
}

const ContactComponent1 = ({
  containerProps,
  titleText = "Get in Touch",
  titleProps,
  subtitleText,
  subtitleProps,
  contentGridProps,
  formContainerProps,
  formFieldProps,
  formRows = [],
  submitButtonProps,
  submitButtonLabel = "SUBMIT",
  mapContainerProps,
  mapSrc,
  mapTitle = "Location Map",
  showSocialButton = false,
  socialButtonLink,
  socialButtonLabel = "Contact on WhatsApp",
  socialButtonProps,
}: ContactComponent1Props) => {
  const { success, error: errorToast } = useAppToast();
  const [submitContact, { isLoading }] = useSubmitContactUsMutation();

  const allFields = useMemo(
    () => formRows.flatMap((row) => row.fields),
    [formRows],
  );

  const validationSchema = useMemo(
    () => buildValidationSchema(allFields),
    [allFields],
  );

  const initialValues = useMemo(
    () =>
      allFields.reduce<Record<string, string>>((acc, { name }) => {
        acc[name] = "";
        return acc;
      }, {}),
    [allFields],
  );

  return (
    <Box {...containerProps}>
      <Text {...titleProps}>{titleText}</Text>
      {subtitleText && <Text {...subtitleProps}>{subtitleText}</Text>}

      <Grid {...contentGridProps}>
        <AppForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          formProps={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            ...formContainerProps,
          }}
          onSubmit={(values, { resetForm }) => {
            const { full_name, phone, message, ...rest } = values as Record<
              string,
              string
            >;
            const extra_fields = Object.keys(rest).length ? rest : undefined;
            submitContact({ full_name, phone, message, extra_fields })
              .unwrap()
              .then(() => {
                success(
                  "Your message has been sent. We'll get back to you soon.",
                );
                resetForm();
              })
              .catch((e) => {
                catchErrorHandler(e, errorToast);
              });
          }}
        >
          {formRows.map((row, rowIndex) => {
            const renderField = ({
              fieldContainerProps,
              ...field
            }: ContactFormFieldConfig) => {
              const fieldEl = (
                <FormField
                  key={field.name}
                  {...formFieldProps}
                  {...(field as any)}
                />
              );
              return fieldContainerProps ? (
                <Box key={field.name} {...fieldContainerProps}>
                  {fieldEl}
                </Box>
              ) : (
                fieldEl
              );
            };

            if (row.fields.length > 1 || row.rowGridProps) {
              return (
                <Grid key={rowIndex} {...row.rowGridProps}>
                  {row.fields.map(renderField)}
                </Grid>
              );
            }

            return row.fields[0] ? renderField(row.fields[0]) : null;
          })}

          <Button
            type="submit"
            isLoading={isLoading}
            isDisabled={isLoading}
            {...submitButtonProps}
          >
            {submitButtonLabel}
          </Button>

          {showSocialButton && socialButtonLink && (
            <Button
              as="a"
              href={socialButtonLink}
              target="_blank"
              rel="noopener noreferrer"
              mt="0.5rem"
              leftIcon={<FaWhatsapp size="1.2em" />}
              bg="#25D366"
              color="white"
              fontWeight="700"
              borderRadius="full"
              _hover={{ bg: "#1EBE5D" }}
              _active={{ bg: "#1AA851" }}
              {...socialButtonProps}
            >
              {socialButtonLabel}
            </Button>
          )}
        </AppForm>

        {mapSrc && (
          <Box {...mapContainerProps}>
            <iframe
              src={mapSrc}
              title={mapTitle}
              width="100%"
              height="100%"
              style={{ border: 0, display: "block" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Box>
        )}
      </Grid>
    </Box>
  );
};

export default ContactComponent1;
